import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, User, Building, Mail, Phone, Send } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { submitToBlackOps, captureAttribution } from '../utils/leadCapture';
import TurnstileWidget from '../components/TurnstileWidget';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_REQUIRED_MESSAGE } from '../utils/turnstile';
import { formatPhoneNumber, formatPhoneForCRM, isValidPhoneNumber } from '../utils/phoneFormatter';

const ShowLeadsPage = () => {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    company: '',
    email: '',
    mobilephone: '',
    role: [] as string[],
    product_interest: [] as string[],
    crops: [] as string[],
    current_pain_point: '',
    purchase_timeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const turnstile = useTurnstile();

  const productOptions = ['Sulfur Burners', 'Hydrocoolers', 'DPA Drenchers', 'Filtration'];
  const cropOptions = ['Cherries', 'Apples', 'Pears', 'Hops', 'Berries', 'Grapes', 'Nuts', 'Citrus', 'Greenhouse', 'Cannabis', 'Other'];
  const roleOptions = ['Grower', 'Packer / Warehouse', 'Orchard Manager', 'Farm Owner', 'Fieldman / PCA / Consultant', 'Irrigation Manager', 'Filtration Dealer', 'Other'];
  const timelineOptions = ['Immediate', '1-3 months', '3-6 months', '6-12 months', 'Not sure'];

  const handleCheckboxChange = (field: 'role' | 'product_interest' | 'crops', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, mobilephone: formatted }));
  };

  const resetForm = () => {
    setFormData({
      firstname: '',
      lastname: '',
      company: '',
      email: '',
      mobilephone: '',
      role: [],
      product_interest: [],
      crops: [],
      current_pain_point: '',
      purchase_timeline: ''
    });
    setError('');
    setIsSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.firstname.trim()) {
      setError('First name is required');
      return;
    }

    if (!formData.lastname.trim()) {
      setError('Last name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!turnstile.token) {
      setError(TURNSTILE_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);

    try {
      const messageParts = [
        formData.role.length ? 'Role: ' + formData.role.join('; ') : '',
        formData.crops.length ? 'Crops: ' + formData.crops.join('; ') : '',
        formData.current_pain_point ? 'Pain point: ' + formData.current_pain_point : '',
        formData.purchase_timeline ? 'Timeline: ' + formData.purchase_timeline : '',
      ].filter(Boolean);

      const payload = {
        form_type: 'contact' as const,
        form_id: 'show_leads_default',
        first_name: formData.firstname,
        last_name: formData.lastname,
        email: formData.email,
        company_name: formData.company || undefined,
        phone: formData.mobilephone ? formatPhoneForCRM(formData.mobilephone) : undefined,
        product_interest: formData.product_interest.length > 0 ? formData.product_interest.join('; ') : undefined,
        message: messageParts.length > 0 ? messageParts.join('\n') : undefined,
        source: 'Show Leads Form',
        turnstile_token: turnstile.token,
        extra_fields: {
          role: formData.role,
          crops: formData.crops,
          current_pain_point: formData.current_pain_point || null,
          purchase_timeline: formData.purchase_timeline || null,
        },
        ...captureAttribution(),
      };

      const result = await submitToBlackOps(payload);

      if (result.success) {
        setIsSuccess(true);

        // Reset form after 7 seconds
        setTimeout(() => {
          resetForm();
        }, 7000);
      } else {
        console.error('Lead capture error:', result.error);
        setError('Submission failed. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('An error occurred. Please try again.');
    }

    // Turnstile tokens are single-use — clear it so a second submission works.
    turnstile.reset();
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      <SEOHead
        title="Trade Show Lead Capture - CTC Waterworks"
        description="Trade show contact information form"
        canonicalPath="/showleads"
        noIndex={true}
      />

      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-4xl"
        >
          <AnimatePresence mode="wait">
            {isSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl shadow-2xl p-8 text-center"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-green-600" size={48} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Thank You!
                </h2>
                <p className="text-xl text-gray-600 mb-2">
                  Your information has been submitted successfully.
                </p>
                <p className="text-gray-500">
                  Form will reset in a few seconds...
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark px-8 py-8 flex items-center justify-center">
                  <img
                    src="/CTC_Waterworks.png"
                    alt="CTC Waterworks"
                    className="h-16 lg:h-20 w-auto"
                  />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded">
                      {error}
                    </div>
                  )}

                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstname" className="block text-sm font-bold text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          id="firstname"
                          value={formData.firstname}
                          onChange={(e) => setFormData(prev => ({ ...prev, firstname: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                          placeholder="First name"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="lastname" className="block text-sm font-bold text-gray-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          id="lastname"
                          value={formData.lastname}
                          onChange={(e) => setFormData(prev => ({ ...prev, lastname: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                          placeholder="Last name"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email and Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                          placeholder="email@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="mobilephone" className="block text-sm font-bold text-gray-700 mb-2">
                        Mobile Phone
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="tel"
                          id="mobilephone"
                          value={formData.mobilephone}
                          onChange={handlePhoneChange}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-bold text-gray-700 mb-2">
                      Company Name
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                        placeholder="Company name"
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Role
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {roleOptions.map(option => (
                        <label
                          key={option}
                          className={`flex items-center px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.role.includes(option)
                              ? 'border-ctc-blue bg-sky-50 font-semibold'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.role.includes(option)}
                            onChange={() => handleCheckboxChange('role', option)}
                            className="h-5 w-5 text-ctc-blue rounded focus:ring-ctc-blue mr-3"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Product Interest */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Product Interest
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {productOptions.map(option => (
                        <label
                          key={option}
                          className={`flex items-center px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.product_interest.includes(option)
                              ? 'border-ctc-blue bg-sky-50 font-semibold'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.product_interest.includes(option)}
                            onChange={() => handleCheckboxChange('product_interest', option)}
                            className="h-5 w-5 text-ctc-blue rounded focus:ring-ctc-blue mr-3"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Crops */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Crops
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {cropOptions.map(option => (
                        <label
                          key={option}
                          className={`flex items-center px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.crops.includes(option)
                              ? 'border-ctc-blue bg-sky-50 font-semibold'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.crops.includes(option)}
                            onChange={() => handleCheckboxChange('crops', option)}
                            className="h-5 w-5 text-ctc-blue rounded focus:ring-ctc-blue mr-3"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Current Pain Point */}
                  <div>
                    <label htmlFor="current_pain_point" className="block text-sm font-bold text-gray-700 mb-2">
                      Current Pain Point
                    </label>
                    <textarea
                      id="current_pain_point"
                      value={formData.current_pain_point}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_pain_point: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                      placeholder="What challenges are you facing?"
                    />
                  </div>

                  {/* Purchase Timeline */}
                  <div>
                    <label htmlFor="purchase_timeline" className="block text-sm font-bold text-gray-700 mb-2">
                      Purchase Timeline
                    </label>
                    <select
                      id="purchase_timeline"
                      value={formData.purchase_timeline}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchase_timeline: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ctc-blue focus:ring-2 focus:ring-ctc-blue/20 transition-all text-lg"
                    >
                      <option value="">Select timeline</option>
                      {timelineOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <TurnstileWidget
                    ref={turnstile.ref}
                    onToken={turnstile.setToken}
                    action="show_leads_default"
                  />

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        <span>Submit</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default ShowLeadsPage;
